import { type Dispatch, type SetStateAction, type FC } from 'react';

import { Fields as SummaryStepFields } from '@sitecore/types/SummaryStep';

import { useSummaryOfferCard } from './useSummaryOfferCard';
import { FlowConsumerContextEnergy } from '../../../../context';
import { ExpandableOfferSection, Ribbon } from '../../../../shared/components';
import { EnergyType, Offer } from '../../../../shared/types';
import { PriceDetailsOverview } from '../../../components';
import { PromotionCard } from '../../../components/ProductCard/PromotionCard';

export interface SummaryOfferCardProps {
  offer: Offer;
  energyType?: EnergyType;
  textualData?: SummaryStepFields;
  flowContext?: FlowConsumerContextEnergy;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * exp-n-522 summary sidebar card: PromotionCard + SummaryStep fields.
 * Kept separate from {@link DefaultOfferCard} so OfferStep-only Sitecore fields
 * (giftData, TOU dialog) are not cast/assumed here.
 */
export const SummaryOfferCard: FC<SummaryOfferCardProps> = ({
  offer,
  energyType = EnergyType.Electricity,
  textualData,
  flowContext,
  isOpen,
  setOpen,
}) => {
  const { productDetails, cashbackTexts, cashbackRibbonTitle, textualDataKey, setCostPeriod, price } =
    useSummaryOfferCard({ offer, energyType, textualData });

  if (!productDetails) {
    return null;
  }

  const { title, promotionText, usps } = productDetails;

  return (
    <PromotionCard
      id={offer.type ?? ''}
      title={title}
      tag={offer.mostPopularChoice ? promotionText : undefined}
      usps={usps}
      price={price}
      isOpen={isOpen}
      ribbon={
        cashbackTexts ? (
          <Ribbon
            size="small"
            emphasis="high"
            title={cashbackRibbonTitle ?? ''}
            text={cashbackTexts.text}
            trigger={cashbackTexts.trigger}
          />
        ) : null
      }>
      <ExpandableOfferSection
        isOpen={isOpen}
        setOpen={setOpen}
        offer={offer}
        textualData={textualData}
        flowContext={flowContext}
        setCostPeriod={setCostPeriod}
        textualDataKey={textualDataKey}
        showProductName={false}
        showEndOfContractDialog={false}>
        {flowContext && <PriceDetailsOverview flowContext={flowContext} textualData={textualData} offer={offer} />}
      </ExpandableOfferSection>
    </PromotionCard>
  );
};
